import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import io from 'socket.io-client';

import { isSessionExpiredError } from 'utils/authError';

axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem("xsrfToken");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // ignore
  }
  return config;
});

const abortControllers = {};
let currentSocket = null;
let currentRoom = {};

const cache = {};
const CACHE_DURATION = 5 * 60 * 1000;
const cacheableEndpoints = [
  'admin/searchMission',
  'admin/getTempWorkerPostulateMission',
  'admin/getMissionDetailsById',
  'admin/getTempWorkerInviteMission',
];

const generateCacheKey = (url, data) => {
  const dataString = JSON.stringify(data || {});
  return `${url}|${dataString}`;
};

const updateCacheEntry = (url, data, newData) => {
  const cacheKey = generateCacheKey(url, data);
  cache[cacheKey] = {
    data: newData,
    timestamp: Date.now(),
  };
};

const isSameCachedMission = (mission, missionId) => {
  const id = missionId?.toString?.() || missionId;
  const realId = mission?.id_mission_real?.toString?.() || mission?.id_mission_real;
  const mongoId = mission?._id?.toString?.() || mission?._id;
  return Boolean(id && (realId === id || mongoId === id));
};

const patchSearchMissionCache = (missionId, patchMission) => {
  Object.keys(cache).forEach((key) => {
    if (!key.startsWith('admin/searchMission|')) return;

    const cacheEntry = cache[key];
    const cachedData = cacheEntry?.data;
    const cachedMissions = Array.isArray(cachedData)
      ? cachedData
      : cachedData?.missions;
    if (!Array.isArray(cachedMissions)) return;

    let hasPatchedMission = false;
    const patchedMissions = cachedMissions.map((mission) => {
      if (!isSameCachedMission(mission, missionId)) return mission;
      hasPatchedMission = true;
      return patchMission(mission);
    });

    if (!hasPatchedMission) return;

    cache[key] = {
      data: Array.isArray(cachedData)
        ? patchedMissions
        : { ...cachedData, missions: patchedMissions },
      timestamp: Date.now(),
    };
  });
};

const isCacheValid = (cacheEntry) => {
  if (!cacheEntry) return false;
  return Date.now() - cacheEntry.timestamp < CACHE_DURATION;
};

const connectSocketWithToken = async () => {
  try {
    const token = localStorage.getItem('xsrfToken');
    if (token) {
      const decodedToken = jwtDecode(token);

      const newRoom = {
        userId: decodedToken.id || '',
      };

      // Vérifier si le socket actuel est connecté à la bonne salle
      if (
        currentSocket &&
        currentSocket?.connected &&
        currentRoom.userId === newRoom.userId
      ) {
        console.log('Socket déjà connecté à la bonne salle');
        return currentSocket;
      }

      // Si le socket existe mais n'est pas connecté à la bonne salle, le déconnecter
      if (currentSocket && !currentSocket?.connected) {
        console.log('Déconnexion du socket actuel');
        currentSocket.disconnect();
      }

      const socket = io(process.env.REACT_APP_API_URL_SOCKET, {
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
        transports: ['websocket'],
        transportOptions: {
          polling: {
            extraHeaders: {
              Authorization: `Bearer ${token}`,
            },
          },
        },
      });

      // Attendre la connexion du socket
      await new Promise((resolve, reject) => {
        socket.on('connect', async () => {
          try {
            await socket.emit('joinRoom', newRoom);
            resolve(); // Résoudre la promesse une fois terminé
          } catch (error) {
            reject(error); // Rejeter en cas d'erreur
          }
        });

        // Gestion d'erreur si le socket échoue à se connecter
        socket.on('connect_error', (err) => {
          reject(new Error(`Erreur de connexion : ${err.message}`));
        });
      });

      currentSocket = socket;
      currentRoom = newRoom;
      return socket;
    }
    return null;
  } catch (error) {
    console.error('Erreur lors de la connexion Socket.IO :', error);
    throw error; // Relancer l'erreur si nécessaire
  }
};

const disconnectSocket = () => {
  if (currentSocket) {
    currentSocket.disconnect();
    currentSocket = null;
    currentRoom = {};
    console.log('Socket déconnecté');
  }
};

/** Avoid firing multiple modals when several API calls fail with invalid token. */
let sessionExpiredDispatched = false;

const dispatchSessionExpired = () => {
  if (sessionExpiredDispatched) return;
  sessionExpiredDispatched = true;
  disconnectSocket();
  window.dispatchEvent(new CustomEvent('session-expired'));
};

const renewTokenIfNeededAndConnectSocket = async () => {
  try {
    return await connectSocketWithToken();
  } catch (error) {
    console.error(
      'Erreur lors du renouvellement du token et de la connexion Socket.IO :',
      error
    );
  }
};

const getAbortController = (key) => {
  if (abortControllers[key]) {
    abortControllers[key].abort();
  }
  abortControllers[key] = new AbortController();
  return abortControllers[key];
};

/** Remove legacy body/query token; auth is Bearer via axios interceptor (except URL token for postRoleUser). */
const stripLegacyXsrfTokenFromPayload = (endpointPath, payload) => {
  if (endpointPath === 'user/role') return payload;
  if (payload == null || typeof payload !== 'object' || Array.isArray(payload))
    return payload;
  if (!Object.prototype.hasOwnProperty.call(payload, 'token')) return payload;
  const { token: _t, ...rest } = payload;
  return rest;
};

const makeRequest = async (
  url,
  method,
  data,
  resolve,
  reject = () => {},
  options = {}
) => {
  const { skipCache = false, abortKey = url } = options;
  data = stripLegacyXsrfTokenFromPayload(url, data);

  try {
    const controller = getAbortController(abortKey);
    const isCacheable = cacheableEndpoints.some((endpoint) =>
      url.startsWith(endpoint)
    );
    const cacheKey = isCacheable ? generateCacheKey(url, data) : null;

    if (
      isCacheable &&
      cacheKey &&
      !skipCache &&
      cache[cacheKey] &&
      isCacheValid(cache[cacheKey])
    ) {
      resolve(cache[cacheKey].data);
      return;
    }

    const requestConfig = {
      method,
      url: `${process.env.REACT_APP_API_URL}${url}`,
      withCredentials: true,
      signal: controller.signal,
    };

    if (method.toLowerCase() === 'get') {
      requestConfig.params = data;
    } else {
      requestConfig.data = data;
    }

    const response = await axios(requestConfig);

    if (response.data.errors) {
      reject(response.data.errors);
    } else {
      if (isCacheable && cacheKey) {
        cache[cacheKey] = {
          data: response.data,
          timestamp: Date.now(),
        };
      }
      resolve(response.data);
    }
  } catch (error) {
    if (isSessionExpiredError(error)) {
      dispatchSessionExpired();
    }
    reject(error);
  }
};

const cleanUpCache = () => {
  const now = Date.now();
  Object.keys(cache).forEach((key) => {
    if (now - cache[key].timestamp > CACHE_DURATION) {
      delete cache[key];
    }
  });
};

setInterval(cleanUpCache, 60 * 1000);

connectSocketWithToken();

const ApiDatabase = {
  getUserProfile: (payload, resolve, reject, options = {}) =>
    makeRequest('user/info', 'post', payload, resolve, reject, options),
  getTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest('tempWorker/info', 'post', payload, resolve, reject, options),
  postRoleUser: (payload, resolve, reject, options = {}) =>
    makeRequest('user/role', 'post', payload, resolve, reject, options),
  postPasswordRefresh: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'user/update/password',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  postEmailRefresh: (payload, resolve, reject, options = {}) =>
    makeRequest('user/update/email', 'put', payload, resolve, reject, options),
  postResendEmailToken: (payload, resolve, reject, options = {}) =>
    makeRequest('resend/tokenMail', 'post', payload, resolve, reject, options),
  postCreateTokenMail: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'resend/createToken',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getListLanguages: (resolve, reject, options = {}) =>
    makeRequest('language', 'get', {}, resolve, reject, options),
  postUserLanguage: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/create/language',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  deleteUserLanguage: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/languages',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  getListTools: (resolve, reject, options = {}) =>
    makeRequest('tools', 'get', {}, resolve, reject, options),
  postUserTool: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/create/tools',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postUserToolDelete: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/tools',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  deleteUserCV: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/cv',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  getInfoMission: (payload, resolve, reject, options = {}) =>
    makeRequest(`mission/info`, 'get', payload, resolve, reject, options),
  postCandidate: (payload, resolve, reject, options = {}) =>
    makeRequest('mission/addUser', 'post', payload, resolve, reject, options),
  postVerifyUser: (payload, resolve, reject, options = {}) =>
    makeRequest('mission/verify', 'post', payload, resolve, reject, options),
  deleteUserMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'mission/deleteUser',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  postCreateExp: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/create/experience',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  putUpdateExp: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/update/experience',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  postDeleteExp: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/experiences',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  getAllLicence: (resolve, reject, options = {}) =>
    makeRequest('licence', 'get', {}, resolve, reject, options),
  postDeleteLicence: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/licence',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  postDeleteFormation: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/training',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdateIban: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/update/bank',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdatePhone: (payload, resolve, reject, options = {}) =>
    makeRequest('user/update/phone', 'put', payload, resolve, reject, options),
  postUpdateAddress: (payload, resolve, reject, options = {}) =>
    makeRequest('address/add', 'post', payload, resolve, reject, options),
  postUpdateAddressTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/address/add', 'post', payload, resolve, reject, options),
  getAddress: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `address/${payload.id}`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheets: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/timeSheets/allInfo',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetInfo: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `timeSheets/${payload.id}`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetDay: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `timeSheets/getTimeSheetDay/${payload.id}`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetHistory: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `timeSheets/${payload.id}/history`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdateDay: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'timeSheets/updateDayAdmin',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getAllUsersWithIdentityFilesToComplete: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/tempWorkersWithIdentityFilesToComplete',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTempWorkersValidatedByAI: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/tempWorkersValidatedByAI',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getUserWithIdentityFilesToComplete: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/tempWorkerWithIdentityFilesToComplete',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postRefuseTempWorkerFileAdmin: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/refuseTempWorkerFileAdmin',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postAcceptTempWorkerFileAdmin: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/acceptTempWorkerFileAdmin',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTempWorkerById: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `tempWorker/${payload.adminTempWorkerId}`,
      'get',
      {},
      resolve,
      reject,
      options
    ),
  getTempWorkerByIdUser: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `tempWorker/tempWorkerByIdUser/${payload.idUser}`,
      'get',
      {},
      resolve,
      reject,
      options
    ),
  getUserById: (payload, resolve, reject, options = {}) =>
    makeRequest(`user/${payload.id_user}`, 'get', {}, resolve, reject, options),
  postAcceptTempWorkerFileIdentityAdmin: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/acceptTempWorkerFileIdentityAdmin',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postAcceptTempWorkerFileVisaAdmin: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/acceptTempWorkerFileVisaAdmin',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postAcceptTempWorkerFileVitalCardAdmin: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/acceptTempWorkerFileVitalCardAdmin',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postPresignFiles: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/presignFiles',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetsBeSTT: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getTimeSheetsBeSTT',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdateTimeSheet: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'timeSheets/updateCompany',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  updateTimeSheetStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'timeSheets/updateStatus',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanies: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getCompaniesList`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyInfo: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getCompanyInfo`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyMissionsAndTempWorkers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companyMissionsAndTempWorkers`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getMinimumWage: (resolve, reject, options = {}) =>
    makeRequest('admin/getMinimumWage', 'get', {}, resolve, reject, options),
  postCoefficient: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/postCoefficient',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  searchMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/searchMission',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  globalSearch: (payload, resolve, reject) => {
    const { search, limit = 5 } = payload;
    makeRequest(
      'admin/globalSearch',
      'post',
      { search, limit },
      (data) => {
        resolve({
          missions: data?.missions || [],
          tempWorkers: data?.tempWorkers || [],
          companies: data?.companies || [],
          clientContacts: data?.clientContacts || [],
        });
      },
      reject
    );
  },
  getMissionDetailsById: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getMissionDetailsById`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  searchTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/searchTempWorker',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  modifyMissionPeriod: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'mission/modifyMissionPeriod',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  assignTempWorkerToMission: (payload, resolve, reject, options = {}) =>
    makeRequest('mission/assign', 'post', payload, resolve, reject, options),
  requestSignContract: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/requestSignContract',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTempWorkerPostulateMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getTempWorkerPostulateMission',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  unassignMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/unassignMission',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  putDisplayCostEstimate: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/updateDisplayCostEstimate',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getDisplayCostEstimate: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getDisplayCostEstimate`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getScheduleByIdMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getScheduleByIdMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  linkCompanyToBigAccount: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/linkCompanyToBigAccount',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getBigAccounts: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getBigAccounts',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  addBigAccount: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/addBigAccount',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyBigAccount: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getCompanyBigAccount`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  updScheduleByIdMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'mission/updScheduleByIdMission',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  deleteCompanyBigAccount: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/deleteCompanyBigAccount',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  deleteBigAccount: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/deleteBigAccount',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  requestGenerationContract: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/requestGenerationContract',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  regenerateAmendment: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/regenerateAmendment',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postAddCompany: (payload, resolve, reject, options = {}) =>
    makeRequest('company/add', 'post', payload, resolve, reject, options),
  getReasonRefusesDocuments: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/reasonRefusesDocuments`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRatingTreatedTempWorkers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/ratingTreatedTempWorkers`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRatingUntreatedTempWorkers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/ratingUntreatedTempWorkers`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRatingTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/ratingTempWorker`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRatingTempWorkerByMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/ratingTempWorkerByMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postTreatmentRatingTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/treatmentRatingTempWorker',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getNbRatingUntreatedTempWorkers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/nbRatingUntreatedTempWorkers`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getReviewsByTempWorkerGroupByMissions: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/reviewsByTempWorkerGroupByMissions`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getReviewsByTempworkerByMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/reviewsByTempworkerByMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTempWorkerInviteMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getTempWorkerInviteMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getNbReviewsTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/nbReviewsTempWorker`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getNationalities: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/nationalities`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getAllQualifications: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `qualification/getAllQualifications`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postQualification: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'qualification/postQualification',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  updateQualification: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'qualification/putQualification',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getQualificationByID: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `qualification/getQualificationByID`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  delQualification: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'qualification/delQualification',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  activateQualification: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'qualification/activateQualification',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getReasonExcludeAgency: (payload, resolve, reject, options = {}) =>
    makeRequest('vivier/reasonExcludeAgency', 'get', payload, resolve, reject, options),
  postExcludeAgency: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'vivier/postExcludeAgency',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getLocalisationPreferenceUser: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/localisationPreference`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  updLocalisationPreferenceUsers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/localisationPreferenceUsers',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  deleteVivier: (payload, resolve, reject, options = {}) =>
    makeRequest('vivier/delete', 'post', payload, resolve, reject, options),
  deleteVivierUser: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'vivier/deleteCandidateVivier',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  updUserVivierPreferenceStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'vivier/updUserVivierPreferenceStatus',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getCommentsByTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/commentsByTempWorker`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getNbCommentsByTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/nbCommentsByTempWorker`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postCommentTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/commentTempWorker',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  deleteCommentTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/commentTempWorker',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  getVivierById: (payload, resolve, reject, options = {}) =>
    makeRequest(`vivier/vivierById`, 'get', payload, resolve, reject, options),
  updateUsersVivierStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'vivier/updateUsersVivierStatus',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getUserVivierArchiveReason: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/userVivierArchiveReason`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getUserViviers: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/userViviers`, 'get', payload, resolve, reject, options),
  getUserMissions: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/userMissions`, 'get', payload, resolve, reject, options),
  getUserRefusedMissions: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/userRefusedMissions`, 'get', payload, resolve, reject, options),
  getUserUnassignedMissions: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/userUnassignedMissions`, 'get', payload, resolve, reject, options),
  getUserCandidatures: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/userCandidatures`, 'get', payload, resolve, reject, options),
  getReasonRefusesMission: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/reasonRefusesMission`, 'get', payload, resolve, reject, options),
  getReasonUnassignMission: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/reasonUnassignMission`, 'get', payload, resolve, reject, options),
  getCollectiveAgreement: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/collectiveAgreement`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  deleteAdminVivierFavorite: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/adminVivierFavorite',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  searchCompanyInfosBySiret: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/searchCompanyInfosBySiret`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postAdminVivierFavorite: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/adminVivierFavorite',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  searchCompanyInfosByName: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/searchCompanyInfosByName`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetHoursVerified: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getTimeSheetHoursVerified',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getVivierListUsers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `vivier/vivierListUser`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getVivierAllPage: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `vivier/vivierAllPage`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getVivierFiche: (payload, resolve, reject, options = {}) =>
    makeRequest(`vivier/vivierFiche`, 'get', payload, resolve, reject, options),
  getMissionVivier: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `vivier/missionVivier`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getLogVivier: (payload, resolve, reject, options = {}) =>
    makeRequest(`vivier/logVivier`, 'get', payload, resolve, reject, options),
  getVivierAll: (payload, resolve, reject, options = {}) =>
    makeRequest(`vivier/vivierAll`, 'get', payload, resolve, reject, options),
  postCancelMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/cancelMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postDeleteMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/deleteMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getInfoMissionCancelDelete: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `mission/infoMissionCancelDelete`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  updateMissionStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/updateMissionStatus`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  searchMissionCompanies: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/searchMissionCompanies`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  searchMissionAssignedTempWorkers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/searchMissionAssignedTempWorkers`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getReasonsMissionSuspension: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getReasonsMissionSuspension`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getDepartmentMissions: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/departmentMissions`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanySuppliers: (payload, resolve, reject, options = {}) =>
    makeRequest('companySupplier', 'get', payload, resolve, reject, options),
  getCityDepartments: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `city/departments`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getCitiesByDepartments: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `city/citiesByDepartments`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postTempWorkerAssignationRefused: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/tempWorkerAssignationRefused`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postTempWorkerPendingContact: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/tempWorkerPendingContact`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  putTempWorkerAssignationRefused: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/tempWorkerAssignationRefused`,
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  postDeleteContractHistory: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/deleteContractHistory`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getQualificationsActive: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `qualification/getQualificationsActive`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTypesQualifications: (payload, resolve, reject) => {
    axios({
      method: 'get',
      url: `${process.env.REACT_APP_API_URL}qualification/getTypesQualifications`,
      withCredentials: true,
    })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err));
  },
  postMissionDetail: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postMissionDetail`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postDuplicateMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/duplicateMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postViviersMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postViviersMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postViviersMissionNotification: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postViviersMissionNotification`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postViviersMissionNotificationGlobal: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/postViviersMissionNotificationGlobal`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getVivierNotificationMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getVivierNotificationMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getLastMissionNotificationGlobal: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getLastMissionNotificationGlobal`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getDescriptionCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getDescriptionCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postDescriptionCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postDescriptionCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdateCompanyOverview: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postUpdateCompanyOverview`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postAddCompanyMember: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postAddCompanyMember`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyEmployee: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getCompanyEmployee`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postEditCompanyMember: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postEditCompanyMember`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postDelCompanyMember: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postDelCompanyMember`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postSendMailLinkCompanyEmployee: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postSendMailLinkCompanyEmployee`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getHubSpotContactByEmail: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getHubSpotContactByEmail`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postNightHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postNightHoursCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getNightHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getNightHoursCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getOvertimesCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getOvertimesCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postOvertimesCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postOvertimesCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getSundayHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getSundayHoursCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postSundayHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postSundayHoursCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getHolidayHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getHolidayHoursCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postHolidayHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postHolidayHoursCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getHolidays: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/getHolidays', 'get', payload, resolve, reject, options),
  getCollectiveInstallations: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/getCollectiveInstallations', 'get', payload, resolve, reject, options),
  getCollectiveInstallationsCompany: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/collectiveInstallationsCompany', 'get', payload, resolve, reject, options),
  postCollectiveInstallationsCompany: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/collectiveInstallationsCompany', 'post', payload, resolve, reject, options),
  getRestaurantTicketCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getRestaurantTicketCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRestaurantTicketModalityListCompany: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/getRestaurantTicketModalityListCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postRestaurantTicketCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postRestaurantTicketCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getPostItCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getPostItCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postPostItCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postPostItCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getBonusListCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getBonusListCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getBonusUnitListCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getBonusUnitListCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postAddBonusesAndAllowances: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postAddBonusesAndAllowances`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdBonusesAndAllowances: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postUpdBonusesAndAllowances`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getBonusAndAllowance: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getBonusAndAllowance`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postDelBonusesAndAllowances: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postDelBonusesAndAllowances`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getBonusesAndAllowancesOtherCompany: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/getBonusesAndAllowancesOtherCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postBonusesAndAllowancesOtherCompany: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/postBonusesAndAllowancesOtherCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getEmployeesToAssignMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getEmployeesToAssignMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postMissionAdminEmployeeAssigned: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postMissionAdminEmployeeAssigned`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postImportedTimeSheet: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postImportedTimeSheet`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getCompaniesForMissionCreation: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/creationMission/getCompaniesForMissionCreation`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyQualificationSuggestionsFromRecentMissions: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/creationMission/getCompanyQualificationSuggestionsFromRecentMissions`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRecentContactNamesForMissionCreation: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/creationMission/getRecentContactNamesForMissionCreation`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getListEmployeesForMissionCreation: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/creationMission/getListEmployeesForMissionCreation`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  createMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/creationMission/createMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  inviteUsersToMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/creationMission/inviteUsersToMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTempWorkersRatingByMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/creationMission/getTempWorkersRatingByMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  listPayslips: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/listPayslips', 'get', payload, resolve, reject, options),
  listPayslipsByWorker: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/listPayslipsByWorker', 'get', payload, resolve, reject, options),
  importPayslips: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/importPayslips',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetAnalytics: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/timeSheetAnalytics',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  deletePayslips: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/deletePayslips',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getPayslipImportStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/payslipImportStatus/${payload.id}`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getPayslipImportHistory: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/payslipImportHistory',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  exportPayslipsCSV: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/exportPayslipsCSV',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  payslipStats: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/payslipStats', 'get', payload, resolve, reject, options),
  listInvoices: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/listInvoices', 'get', payload, resolve, reject, options),
  listInvoicesByCompany: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/listInvoicesByCompany', 'get', payload, resolve, reject, options),
  importInvoices: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/importInvoices', 'post', payload, resolve, reject, options),
  deleteInvoices: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/deleteInvoices', 'post', payload, resolve, reject, options),

  getInvoiceImportStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/invoiceImportStatus/${payload.id}`, 'get', payload, resolve, reject, options),
  getInvoiceImportHistory: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/invoiceImportHistory', 'get', payload, resolve, reject, options),
  invoiceStats: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/invoiceStats', 'get', payload, resolve, reject, options),
  exportInvoicesCSV: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/exportInvoicesCSV', 'get', payload, resolve, reject, options),
  getInvoicesNotOnPennyLane: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/invoicesNotOnPennyLane', 'get', payload, resolve, reject, options),
  pushInvoicesToPennyLane: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/pushInvoicesToPennyLane', 'post', payload, resolve, reject, options),
  getAdminTeam: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/getAdminTeam', 'get', payload, resolve, reject, options),
  getDeactivatedAdminTeam: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/getDeactivatedAdminTeam', 'get', payload, resolve, reject, options),
  deactivateAdminTeamMember: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/deactivateAdminTeamMember', 'put', payload, resolve, reject, options),
  reactivateAdminTeamMember: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/reactivateAdminTeamMember', 'put', payload, resolve, reject, options),
  postLogMissionClick: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/postLogMissionClick', 'post', payload, resolve, reject, options),
  getMissionActivityLogs: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/getMissionActivityLogs', 'get', payload, resolve, reject, options),
  postAmendmentRequestAction: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/amendmentRequestAction',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyActivityLogs: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getCompanyActivityLogs',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postCompanyJustificatif: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/uploadCompanyJustificatif',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyActivityJustificatif: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getCompanyActivityJustificatif',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getPennyLaneCategories: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/pennylane/categories', 'get', payload, resolve, reject, options),
  postPennyLaneCategory: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/pennylane/categories', 'post', payload, resolve, reject, options),
  connectSocketWithToken,
  disconnectSocket,
  getSocket: () => currentSocket,
  cache,
  cacheableEndpoints,
  generateCacheKey,
  patchSearchMissionCache,
  updateCacheEntry,
};

export { dispatchSessionExpired };
export default ApiDatabase;
